import { useCallback, useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";
import { useFeature } from "../features";
import {
  useBAccount,
  recipients,
  useInstitution,
  achCompanies,
  useIsBusinessUser,
  limits,
  useAccounts,
  achDualApprovalRequired,
  dualApprovalRequired,
  useUser,
} from "../../entities";
import { useLibrary } from "../../../providers";
import filters from "../../../../filters";
import { getTotalCountFromRecurrenceString } from "../../../../Recurrence";
import OrganizationUser from "../../../../OrganizationUser";
import Organization from "../../../../Organization";
import { useFetchOrganizationUsers } from "../../entities/organizations";

import {
  ACHPaymentForm,
  getAchScheduledPaymentDetailsSchema,
  getAchScheduledAdditionalDetailsSchema,
} from "./ACHPayment.form";
import { useManageRecipientPermission } from "./permissions";
import {
  formatValuesForRecurrenceString,
  getIsOneTimePaymentForToday,
} from "./utils";

export const useMappedEntryDescription = () => {
  const { features } = useFeature();
  const entryDescriptionOptions =
    features.allowed_ach_payment_entry_descriptions || [];

  const toSentenceCase = (value: string) => {
    return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const mappedOptions = entryDescriptionOptions.map((option) => ({
    display: toSentenceCase(option),
    value: option,
  }));

  const getSelectedEntryDescription = (value: string) =>
    mappedOptions.find((opt) => opt.value === value)?.display || value;

  return { mappedOptions, getSelectedEntryDescription };
};

export const useSECCodes = () => {
  const { features } = useFeature();
  return features.allowed_std_ent_cls_codes || [];
};

export const useShouldShowSingleACHPaymentFlow = () => {
  const { features } = useFeature();

  return !!(features.ach_payments || features.ach_payments_collect);
};

export const useIsDebitACHPayment = () => {
  const {
    values: { transactionType },
  } = ACHPaymentForm.useForm();

  return transactionType === "debit";
};

export const useAdditionalDetails = () => {
  const institution = useInstitution();
  const sudoModeRequired = institution?.sudo_mode_required_for_ach_payments;
  const form = ACHPaymentForm.useForm();
  const {
    submitForm,
    setValidationSchema,
    validationSchema: existingSchema,
  } = form;
  const recipient = recipients.useSelectedRecipient();
  const { clearAllToasts } = useLibrary("toasts");

  useEffect(() => {
    const schema = getAchScheduledAdditionalDetailsSchema(existingSchema);
    setValidationSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onContinue = useCallback(
    async (next: () => void, trySudo: () => void) => {
      const { success } = await submitForm();
      if (!success) {
        return null;
      }

      clearAllToasts();

      if (!sudoModeRequired) {
        return next();
      }

      return trySudo();
    },
    [clearAllToasts, submitForm, sudoModeRequired],
  );

  return useObjectMemo({
    onContinue,
    recipient,
    form,
  });
};

export const usePaymentDetails = () => {
  const institution = useInstitution();
  const isBusinessUser = useIsBusinessUser();
  const achUseAvailableBalanceForScheduledPush =
    institution?.ach_use_available_balance_for_scheduled_push;
  const form = ACHPaymentForm.useForm();
  const limitsForDisplay = limits.useLimits();
  const accounts = useAccounts();
  const { submitForm, setValidationSchema, values } = form;
  const recipient = recipients.useSelectedRecipient();
  const { clearAllToasts } = useLibrary("toasts");
  const isOneTimePaymentForToday = getIsOneTimePaymentForToday(
    values,
    institution?.ach_next_cutoff,
  );

  const applyValidationSchema = useCallback(() => {
    const schema = getAchScheduledPaymentDetailsSchema({
      limitsForDisplay,
      values,
      isOneTimePaymentForToday,
      achUseAvailableBalanceForScheduledPush: Boolean(
        achUseAvailableBalanceForScheduledPush,
      ),
      isBusinessUser,
      accounts,
    });

    setValidationSchema(schema);
  }, [
    limitsForDisplay,
    values,
    isOneTimePaymentForToday,
    achUseAvailableBalanceForScheduledPush,
    isBusinessUser,
    accounts,
    setValidationSchema,
  ]);

  useEffect(() => {
    applyValidationSchema();
  }, [applyValidationSchema]);

  const onContinue = useCallback(
    async (next: () => void) => {
      const { success } = await submitForm();

      if (!success) {
        return null;
      }

      clearAllToasts();

      return next();
    },
    [clearAllToasts, submitForm],
  );

  return useObjectMemo({
    onContinue,
    applyValidationSchema,
    recipient,
    form,
  });
};

// specific to mobile, and uses mobile fluent IDs
export const useReviewDetails = () => {
  const { values } = ACHPaymentForm.useForm();
  const {
    fromAccount,
    amount,
    secCode,
    startDate,
    recurringRule,
    description,
    memo,
    entryDesc,
  } = values;
  const { send: fetchACHCompanies } = achCompanies.useFetchACHCompanies();
  const requiresACHDualApproval = useSelector(achDualApprovalRequired);
  const recipient = recipients.useSelectedRecipient();
  const account = useBAccount(fromAccount as API.AccountId);
  const t = useLibrary("translations");
  const achCompany = achCompanies.useACHCompany();

  const { calculateTotalPayments } = useCalculateTotalPayments();
  const numOfPayments = calculateTotalPayments();

  useEffect(() => {
    fetchACHCompanies(`institution_account=${fromAccount}`);
  }, [fromAccount, fetchACHCompanies]);

  return useMemo(() => {
    if (!recipient || !account || !amount || !secCode) {
      return null;
    }

    type Section = {
      header: string;
      rows: Array<{ header: string; value: string }>;
    };

    const unknown = t.getString("ach-payment-review-unknown", {}, "Unknown");

    const totalPayments = calculateTotalPayments();
    const totalAmount = (totalPayments * amount) as Cents;

    const getDisplaySendDate = () => {
      return filters.americanDate(startDate);
    };

    const paymentSchedule = {
      header: t.getString(
        "ach-payment-review-payment-schedule-header",
        {},
        "Payment schedule",
      ),
      rows: [
        {
          header: t.getString(
            "ach-payment-review-start-date",
            {},
            "Start date",
          ),
          value: filters.americanDate(startDate) || "",
        },
        {
          header: t.getString("ach-payment-review-frequency", {}, "Frequency"),
          value: recurringRule?.frequency || "",
        },
        {
          header: t.getString("ach-payment-review-end-date", {}, "End date"),
          value:
            recurringRule?.ends === "on" && recurringRule?.untilDate
              ? filters.americanDate(recurringRule.untilDate)
              : "",
        },
        {
          header: `${numOfPayments} ${t.getString("ach-payment-review-payments-total", {}, "payments total")}`,
          value:
            recurringRule?.ends === "after" || recurringRule?.ends === "on"
              ? filters.currency(totalAmount)
              : "",
        },
      ].filter((row) => !!row.value),
    };

    const paymentDetails = {
      header: t.getString(
        "ach-payment-review-payment-details-header",
        {},
        "Payment details",
      ),
      rows: [
        {
          header: t.getString("ach-payment-review-from ", {}, "From"),
          value: account.getShortDescription(false),
        },
        {
          header: t.getString("ach-payment-review-memo", {}, "Memo"),
          value: description || "",
        },
        {
          header: t.getString("ach-payment-review-date", {}, "Send date"),
          value: requiresACHDualApproval
            ? t.getString("pending-table-one-time-payment", {}, "Upon approval")
            : getDisplaySendDate(),
        },
        {
          header: t.getString(
            "ach-payment-review-transaction-type",
            {},
            "Transaction type",
          ),
          value: t.getString(
            "ach-payment-review-sec-code",
            { secCode },
            `${secCode} payment`,
          ),
        },
        {
          header: t.getString(
            "ach-payment-review-entry-desc",
            {},
            "Entry description",
          ),
          value: entryDesc || "",
        },
        {
          header: t.getString(
            "ach-payment-review-company-name",
            {},
            "Company name",
          ),
          value: achCompany ? achCompany.company_name : unknown,
        },
        {
          header: t.getString(
            "ach-payment-review-company-id",
            {},
            "Company ID",
          ),
          value: achCompany ? achCompany.company_id : unknown,
        },
      ].filter((row) => row.value),
    };

    const internalDetails = {
      header: t.getString(
        "ach-payment-review-internal-details-header",
        {},
        "Internal details",
      ),
      rows: [
        {
          header: t.getString("ach-payment-review-note", {}, "Note"),
          value: memo || "",
        },
      ].filter((row) => row.value),
    };

    const recipientDetails = {
      header: t.getString(
        "ach-payment-review-recipient-details-header",
        {},
        "Recipient details",
      ),
      rows: [
        {
          header: t.getString("ach-payment-review-recipient", {}, "Recipient"),
          value: recipient.name,
        },
        {
          header: t.getString("ach-payment-review-bank", {}, "Bank"),
          value: recipient.ach_destination?.institution_name || unknown,
        },
        {
          header: t.getString(
            "ach-payment-review-account",
            {},
            "Account number",
          ),
          value: recipient.ach_destination?.account_number || unknown,
        },
        {
          header: t.getString(
            "ach-payment-review-routing",
            {},
            "Routing account",
          ),
          value: recipient.ach_destination?.routing_number || unknown,
        },
      ],
    };

    const sections: Array<Section> = [
      paymentDetails,
      recipientDetails,
      internalDetails,
    ];

    return {
      sections,
      paymentSchedule,
      buttonLabel: t.getString("ach-payment-review-action", {}, "Make payment"),
      amount: filters.currency(amount),
      recipient,
    };
  }, [
    account,
    achCompany,
    amount,
    description,
    entryDesc,
    memo,
    calculateTotalPayments,
    numOfPayments,
    recipient,
    recurringRule?.ends,
    recurringRule?.frequency,
    recurringRule?.untilDate,
    requiresACHDualApproval,
    secCode,
    startDate,
    t,
  ]);
};

export const useCalculateTotalPayments = () => {
  const form = ACHPaymentForm.useForm();
  const { values } = form;
  const { recurringRule } = values;

  // only calculate total payments when the repeated payment section is visible
  // and user has toggled either "on" or "after" (not "never"), or user changes start date
  const shouldCalculateTotalPayments =
    recurringRule?.isVisible &&
    (recurringRule?.ends === "on" || recurringRule?.ends === "after");

  const calculateTotalPayments = useCallback(() => {
    if (shouldCalculateTotalPayments) {
      // if user toggles "after" but there is no count, just set total payments to 0
      if (recurringRule?.ends === "after" && !recurringRule?.count) {
        return 0;
      }

      const { frequency, startDate, untilDate, count } =
        formatValuesForRecurrenceString(values);

      const calculatedTotalPayments = getTotalCountFromRecurrenceString(
        frequency,
        startDate,
        untilDate,
        count,
      );

      return calculatedTotalPayments;
    }

    return 0;
  }, [recurringRule, shouldCalculateTotalPayments, values]);

  return useObjectMemo({
    calculateTotalPayments,
  });
};

export const usePermissionedRecipients = () => {
  const hasManageRecipientPermission = useManageRecipientPermission();
  const permissionedRecipients = useSelector(recipients.selectSortedRecipients);

  return permissionedRecipients.filter(
    (r) => hasManageRecipientPermission || !!r.ach_destination,
  );
};

export const useOrganizationHasMultipleApprovalUsers = (
  dualApprovalType?: "ach" | "wire",
) => {
  const currentOrgUser = useUser();
  const [
    organizationHasMultipleApprovalUsers,
    setOrganizationHasMultipleApprovalUsers,
  ] = useState(false);
  const [loading, setLoading] = useState(true);
  const { org_uuid: orgUuid } = currentOrgUser as API.User;

  const { send: fetchOrgUsers } = useFetchOrganizationUsers(
    orgUuid as API.OrganizationId,
  );

  const achRequired = useSelector(achDualApprovalRequired);
  const wireRequired = useSelector(dualApprovalRequired);

  let requiresDualApproval = true;
  if (dualApprovalType === "ach") {
    requiresDualApproval = achRequired;
  } else if (dualApprovalType === "wire") {
    requiresDualApproval = wireRequired;
  }

  const isOrgUserWithEditPerms =
    currentOrgUser?.org_role &&
    OrganizationUser.ROLES_WITH_EDIT_PERMISSION.includes(
      currentOrgUser.org_role,
    );

  useEffect(() => {
    if (!currentOrgUser) {
      setLoading(false);
      return;
    }

    // if dual approval is required, and user is admin/account holder role,
    // then determine if there are multiple org users with dual approval permissions
    if (requiresDualApproval && isOrgUserWithEditPerms) {
      setLoading(true);
      fetchOrgUsers({
        onSuccess: ({ organization_users: fetchedUsers }) => {
          const hasMultipleApprovalUsers =
            Organization.getOrganizationHasMultipleApprovalUsers(fetchedUsers);
          setOrganizationHasMultipleApprovalUsers(hasMultipleApprovalUsers);
          setLoading(false);
        },
        onError: () => {
          setOrganizationHasMultipleApprovalUsers(false);
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useObjectMemo({
    loading,
    requiresDualApproval,
    organizationHasMultipleApprovalUsers,
    isOrgUserWithEditPerms,
  });
};

export const useShowUserNeedsApprovalAlert = (
  dualApprovalType: "ach" | "wire",
) => {
  const {
    organizationHasMultipleApprovalUsers,
    isOrgUserWithEditPerms,
    loading,
    requiresDualApproval,
  } = useOrganizationHasMultipleApprovalUsers(dualApprovalType);

  const showAlert =
    !loading &&
    requiresDualApproval &&
    !organizationHasMultipleApprovalUsers &&
    isOrgUserWithEditPerms;

  return useObjectMemo({
    showAlert,
  });
};
